/**
 * Enkel navigasjons-blokkering (task #304, 2026-07-02).
 *
 * React Router v6 sin `useBlocker`-hook krever data router
 * (createBrowserRouter). Vår app bruker fortsatt `<BrowserRouter>` som
 * ikke støtter useBlocker. Denne modulen implementerer et lettvekts
 * alternativ via en globalt registrert handler.
 *
 * Flyt:
 * 1. En komponent (typisk StudyPlanForm) registrerer sin blockHandler
 *    via `registerNavigationBlock` når skjemaet har ulagrede endringer
 * 2. Handleren mottar den forsøkte navigasjonspath-en. Returnerer `false`
 *    hvis navigasjon skal blokkeres (og komponenten viser egen modal),
 *    eller `true` hvis navigasjon skal fortsette.
 * 3. Layout wrapper alle interne <Link>-klikk med `tryNavigate` som
 *    sjekker den registrerte handleren.
 * 4. `beforeunload` håndteres separat via autosave-hooken.
 *
 * NB: Dette dekker interne navigasjoner (klikk på Link/NavLink) og
 * bakover-knapp håndteres via popstate. Direkte URL-skriving i
 * adresselinjen kan ikke fanges — men det er også et bevisst valg.
 */

type BlockHandler = (attemptedPath: string) => boolean;

let currentHandler: BlockHandler | null = null;

/**
 * Registrer en handler som avgjør om navigasjon skal blokkeres.
 * Returnerer en unregister-funksjon som skal kalles ved unmount.
 */
export function registerNavigationBlock(handler: BlockHandler): () => void {
  currentHandler = handler;
  return () => {
    // Bare fjern hvis det fortsatt er samme handler (unngår race med
    // ny komponent som har registrert etter oss)
    if (currentHandler === handler) {
      currentHandler = null;
    }
  };
}

/**
 * Kalles fra Link-wrappers i Layout. Returnerer true hvis navigasjon
 * skal fortsette (ingen handler eller handler tillater), false hvis
 * blokkert (komponenten viser egen modal).
 */
export function tryNavigate(attemptedPath: string): boolean {
  if (!currentHandler) return true;
  return currentHandler(attemptedPath);
}

/**
 * Tvinger navigasjon uten å sjekke handleren. Brukes internt av
 * komponenter som ønsker å utføre "Forkast og gå"-flyten etter at
 * brukeren har eksplisitt bekreftet i modalen.
 */
export function bypassNext(): void {
  currentHandler = null;
}
